import React from "react";
import { openSharedEventStream } from "../../../../../../shared/frontend/live/sharedEventStream";
import { parseSseFrame } from "../../../../../../shared/frontend/live/sseFrame";
import { createBackgroundRefresh, isBackgroundRefreshPaused } from "../../../../../../shared/frontend/system/backgroundRefresh";
import { loadAuthInfoFromStorage } from "../auth/authStorage";

const EVENT_PING = "ping";
const EVENT_TRANSPORT_RECONNECTING = "mq.transport.reconnecting";
const EVENT_TRANSPORT_RECONNECTED = "mq.transport.reconnected";
const RECONNECT_BACKOFF_FACTOR = 2;

/** Länge des Trenners zwischen zwei Server-Sent-Events: zwei Zeilenumbrüche. */
const SSE_FRAME_DELIMITER_LENGTH = 2;

export interface RealtimeMessage<TPayload = unknown> {
  type: string;
  tenantId?: string | null;
  studioId?: string | null;
  entityId?: string | null;
  action?: string | null;
  payload?: TPayload;
  createdAt?: string | null;
}

export type RealtimeStatus = "idle" | "connecting" | "connected" | "reconnecting" | "closed";

type RealtimeHandler = (message: RealtimeMessage) => void;

interface RealtimeContextValue {
  status: RealtimeStatus;
  subscribe: (type: string, handler: RealtimeHandler) => () => void;
  /**
   * Meldet die Rückkehr nach einem **echten** Verbindungsabriss.
   *
   * Während der Strom weg war, sind Ereignisse verloren gegangen — die Flächen zeigen dann einen
   * Stand, den es nicht mehr gibt, ohne dass etwas darauf hinweist. Abonnenten laden daraufhin
   * einmal neu.
   *
   * Bewusst **nicht** beim ersten Verbinden: das deckt der Abruf beim Betreten der Seite ab.
   */
  subscribeResync: (handler: () => void) => () => void;
}

const RealtimeContext = React.createContext<RealtimeContextValue | null>(null);

const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
const LIST_REFRESH_DEBOUNCE_MS = 300;

function getStoredTenantId(): string {
  try {
    return String(loadAuthInfoFromStorage()?.tenantId ?? "").trim();
  } catch {
    return "";
  }
}

export function RealtimeProvider({
  children,
  token,
  tenantId
}: {
  children: React.ReactNode;
  token: string;
  tenantId?: string | null;
}) {
  const [status, setStatus] = React.useState<RealtimeStatus>("idle");
  const subscribersRef = React.useRef<Map<string, Set<RealtimeHandler>>>(new Map());
  const resyncSubscribersRef = React.useRef<Set<() => void>>(new Set());
  // Merkt, ob schon einmal eine Verbindung stand. Nur dann ist ein erneutes Verbinden eine
  // Rückkehr nach einem Abriss — beim ersten Mal gibt es keine verpassten Ereignisse.
  const hadConnectionRef = React.useRef(false);
  const abortRef = React.useRef<AbortController | null>(null);
  const reconnectTimerRef = React.useRef<number | null>(null);
  const reconnectDelayRef = React.useRef(INITIAL_RECONNECT_DELAY_MS);
  const tokenRef = React.useRef(token);
  const tenantIdRef = React.useRef(tenantId);
  // Der Status wird auch in Ereignis-Rückrufen gebraucht, die nicht neu gebunden werden.
  const statusRef = React.useRef(status);
  tokenRef.current = token;
  tenantIdRef.current = tenantId;
  statusRef.current = status;

  const clearReconnectTimer = React.useCallback(() => {
    if (reconnectTimerRef.current != null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const dispatchMessage = React.useCallback((message: RealtimeMessage) => {
    if (message.type === EVENT_PING) return;
    const handlers = subscribersRef.current.get(message.type);
    if (!handlers || handlers.size === 0) return;
    handlers.forEach((handler) => {
      try {
        handler(message);
      } catch {
        // Realtime handlers are isolated from the stream loop.
      }
    });
  }, []);

  const connect = React.useCallback(() => {
    clearReconnectTimer();
    abortRef.current?.abort();

    const currentToken = tokenRef.current;
    const currentTenantId = (tenantIdRef.current ?? "").trim() || getStoredTenantId();
    if (!currentToken || !currentTenantId) {
      setStatus("idle");
      return;
    }

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStatus((prev) => (prev === "connected" ? "reconnecting" : "connecting"));

    const run = async () => {
      try {
        const res = await openSharedEventStream("/api/v1/live/events", {
          transportEvents: true,
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "X-Tenant-Id": currentTenantId,
            Accept: "text/event-stream"
          },
          signal: ctrl.signal
        });

        if (!res.ok || !res.body) throw new Error(`Realtime stream failed: ${res.status}`);

        setStatus("connected");
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;

        // Rückkehr nach einem Abriss: die Zeit ohne Strom hat Ereignisse verschluckt.
        if (hadConnectionRef.current) {
          resyncSubscribersRef.current.forEach((handler) => {
            try {
              handler();
            } catch {
              // Ein fehlschlagender Abgleich darf den Strom nicht anhalten.
            }
          });
        }
        hadConnectionRef.current = true;

        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (!ctrl.signal.aborted) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

          for (;;) {
            const frameEnd = buffer.indexOf("\n\n");
            if (frameEnd < 0) break;
            const frame = buffer.slice(0, frameEnd);
            buffer = buffer.slice(frameEnd + SSE_FRAME_DELIMITER_LENGTH);
            const parsed = parseSseFrame(frame);
            if (!parsed) continue;
            if (parsed.eventType === EVENT_TRANSPORT_RECONNECTING) {
              statusRef.current = "reconnecting";
              setStatus("reconnecting");
              continue;
            }
            if (parsed.eventType === EVENT_TRANSPORT_RECONNECTED) {
              statusRef.current = "connected";
              setStatus("connected");
              resyncSubscribersRef.current.forEach(handler => {
                try { handler(); } catch { /* One consumer must not interrupt the shared stream. */ }
              });
              continue;
            }
            if (parsed.eventType === EVENT_PING) continue;

            try {
              const message = JSON.parse(parsed.data) as RealtimeMessage;
              dispatchMessage({ ...message, type: message.type || parsed.eventType });
            } catch {
              // Ignore malformed best-effort events.
            }
          }
        }
      } catch {
        if (ctrl.signal.aborted) return;
      }

      if (ctrl.signal.aborted) return;
      setStatus("reconnecting");
      const delay = reconnectDelayRef.current;
      reconnectDelayRef.current = Math.min(delay * RECONNECT_BACKOFF_FACTOR, MAX_RECONNECT_DELAY_MS);
      reconnectTimerRef.current = window.setTimeout(connect, delay);
    };

    void run();
  }, [clearReconnectTimer, dispatchMessage]);

  React.useEffect(() => {
    // Ein Wechsel von Token oder Mandant ist kein Abriss, sondern ein neuer Anfang: die Flächen
    // laden für den neuen Kontext ohnehin frisch. Deshalb hier zurücksetzen, damit die erste
    // Verbindung danach keinen Abgleich auslöst.
    hadConnectionRef.current = false;
    connect();
    return () => {
      clearReconnectTimer();
      abortRef.current?.abort();
      abortRef.current = null;
      setStatus("closed");
    };
  }, [connect, token, tenantId]);

  React.useEffect(() => {
    // Nur neu verbinden, wenn keine Verbindung steht. `connect()` bricht als erstes den laufenden
    // Strom ab — bedingungslos aufgerufen riss das eine gesunde Verbindung nieder und baute sie
    // sofort wieder auf. `visibilitychange` feuert dabei in **beide** Richtungen: auch das
    // Wegschalten des Tabs löste den Abbruch aus, mit einem Fenster verpasster Ereignisse.
    const reconnectIfDisconnected = () => {
      if (!tokenRef.current) return;
      if (statusRef.current === "connected" || statusRef.current === "connecting") return;
      reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
      connect();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      reconnectIfDisconnected();
    };
    window.addEventListener("online", reconnectIfDisconnected);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("online", reconnectIfDisconnected);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [connect]);

  const subscribe = React.useCallback((type: string, handler: RealtimeHandler) => {
    const normalizedType = type.trim();
    if (!normalizedType) return () => {};
    let handlers = subscribersRef.current.get(normalizedType);
    if (!handlers) {
      handlers = new Set<RealtimeHandler>();
      subscribersRef.current.set(normalizedType, handlers);
    }
    handlers.add(handler);
    return () => {
      const current = subscribersRef.current.get(normalizedType);
      if (!current) return;
      current.delete(handler);
      if (current.size === 0) subscribersRef.current.delete(normalizedType);
    };
  }, []);

  const subscribeResync = React.useCallback((handler: () => void) => {
    resyncSubscribersRef.current.add(handler);
    return () => {
      resyncSubscribersRef.current.delete(handler);
    };
  }, []);

  const value = React.useMemo<RealtimeContextValue>(
    () => ({ status, subscribe, subscribeResync }),
    [status, subscribe, subscribeResync]
  );
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtimeStatus(): RealtimeStatus {
  return React.useContext(RealtimeContext)?.status ?? "idle";
}

export function useRealtimeEvent<TPayload = unknown>(
  type: string,
  handler: (message: RealtimeMessage<TPayload>) => void,
  options?: { enabled?: boolean; background?: boolean; onResume?: () => void }
) {
  const ctx = React.useContext(RealtimeContext);
  const handlerRef = React.useRef(handler);
  const onResumeRef = React.useRef(options?.onResume);
  React.useLayoutEffect(() => { handlerRef.current = handler; onResumeRef.current = options?.onResume; });
  const enabled = options?.enabled !== false;
  const subscribe = ctx?.subscribe;
  const background = options?.background === true;

  React.useEffect(() => {
    if (!subscribe || !enabled) return;
    let latest: RealtimeMessage<TPayload> | undefined;
    const refresh = createBackgroundRefresh(() => {
      if (onResumeRef.current) onResumeRef.current();
      else if (latest) handlerRef.current(latest);
    });
    const unsubscribe = subscribe(type, (message) => {
      if (background && isBackgroundRefreshPaused()) {
        latest = message as RealtimeMessage<TPayload>;
        refresh.request();
      } else handlerRef.current(message as RealtimeMessage<TPayload>);
    });
    return () => { unsubscribe(); refresh.dispose(); };
  }, [subscribe, enabled, type, background]);
}

// Flächen, die ihre Ereignisse einzeln über useRealtimeEvent abonnieren, haben noch keinen
// Abgleich nach einem Abriss. Sie bekommen ihn mit der Umstellung auf react-query (Schritt 9 des
// Plans) über invalidateQueries — ein weiterer exportierter Hook an dieser Stelle würde nur eine
// dritte Bauform danebenstellen.

export function useRealtimeListRefresh({
  eventTypes,
  reload,
  debounceMs = LIST_REFRESH_DEBOUNCE_MS,
  background = true,
  enabled = true
}: {
  eventTypes: string[];
  reload: () => void;
  debounceMs?: number;
  /** Rechteabgleiche müssen auch während einer geöffneten Bearbeitung stattfinden. */
  background?: boolean;
  enabled?: boolean;
}) {
  const ctx = React.useContext(RealtimeContext);
  const reloadRef = React.useRef(reload);
  const timerRef = React.useRef<number | null>(null);
  React.useLayoutEffect(() => { reloadRef.current = reload; }, [reload]);
  const subscribe = ctx?.subscribe;
  const subscribeResync = ctx?.subscribeResync;

  React.useEffect(() => {
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (!subscribe || !subscribeResync || !enabled) return;
    const refresh = createBackgroundRefresh(() => reloadRef.current());
    const schedule = () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        if (background) refresh.request();
        else reloadRef.current();
      }, debounceMs);
    };
    const cleanups = eventTypes.map((type) => subscribe(type, schedule));
    // Nach einem Verbindungsabriss sind Ereignisse verloren gegangen. Ohne diesen Abgleich zeigt
    // die Fläche danach still einen veralteten Stand — ohne Ladezustand und ohne Hinweis.
    cleanups.push(subscribeResync(schedule));
    return () => {
      cleanups.forEach((cleanup) => cleanup());
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      timerRef.current = null;
      refresh.dispose();
    };
  }, [subscribe, subscribeResync, debounceMs, background, enabled, eventTypes.join("|")]);
}
